import React, { FC, forwardRef, ReactNode, useState } from 'react';
import { HeaderButton, HeaderButtonProps } from '@/src/components/header/HeaderButton';
import { BellIcon } from '@/src/components/icon/stroke/BellIcon';
import { Fade, ClickAwayListener, ClickAwayListenerProps, Popper, PopperProps, FadeProps } from '@mui/material';
import { NotificationCenter, NotificationCenterProps } from './NotificationCenter';

export interface NotificationCenterHeaderButtonProps extends NotificationCenterProps {
  /**
   * Label for the tooltip or the text button
   */
  label?: HeaderButtonProps['label'];
  /**
   * Change icon button with tooltip into button
   */
  extend?: boolean;
  /**
   * Properties applied to tooltip
   */
  tooltipProps?: HeaderButtonProps['tooltipProps'];
  /**
   * Properties applied to badge
   */
  badgeProps?: HeaderButtonProps['badgeProps'];
  /**
   * Props applied to HeaderButton
   */
  headerButtonProps?: HeaderButtonProps;
  /**
   * Icon for the HeaderButton
   * @default BellIcon
   */
  icon?: ReactNode;
  /**
   * Props for the Popper component
   */
  popperProps?: PopperProps;
  /**
   * Props applied to ClickAwayListener
   */
  clickAwayListenerProps?: Omit<ClickAwayListenerProps, 'children'>;
  /**
   * Props applied to Fade
   */
  fadeProps?: Omit<FadeProps, 'children'>;
}

/**
 * HeaderButton that opens NotificationCenter
 */
export const NotificationCenterHeaderButton: FC<NotificationCenterHeaderButtonProps> = forwardRef(
  (
    {
      popperProps,
      clickAwayListenerProps,
      fadeProps,
      label = 'Notifications',
      extend,
      tooltipProps,
      badgeProps,
      headerButtonProps,
      icon,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const [anchor, setAnchor] = useState(null);
    const { sx: popperSx, ...restPopper } = popperProps ?? {};

    return (
      <>
        <HeaderButton
          label={label}
          extend={extend ?? false}
          tooltipProps={{
            ...(anchor && { open: false }),
            ...tooltipProps,
          }}
          badgeProps={badgeProps}
          onClick={(e) => {
            onClick?.(e);
            setAnchor(e.currentTarget);
          }}
          ref={ref}
          data-testid="NotificationCenterHeaderButton"
          {...headerButtonProps}
        >
          {icon || <BellIcon />}
        </HeaderButton>
        <Popper
          open={!!anchor}
          anchorEl={anchor}
          placement="bottom-end"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 18],
              },
            },
          ]}
          transition
          sx={{
            zIndex: (theme) => theme.zIndex.appBar + 10,
            ...popperSx,
          }}
          {...restPopper}
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={() => setAnchor(null)} {...clickAwayListenerProps}>
              <Fade {...TransitionProps} {...fadeProps}>
                <NotificationCenter {...rest} ref={undefined} />
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </>
    );
  },
);
